import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/* ═══ SCORE IMPACT SIMULATOR ═══ */
const SCORE_MAP = [
  { payment: 100, gain: 12 },
  { payment: 200, gain: 28 },
  { payment: 300, gain: 55 },
  { payment: 400, gain: 68 },
  { payment: 500, gain: 82 },
];
const BASE_SCORE = 647;

function interpolateGain(payment) {
  if (payment <= 100) return SCORE_MAP[0].gain;
  if (payment >= 500) return SCORE_MAP[4].gain;
  for (let i = 0; i < SCORE_MAP.length - 1; i++) {
    const a = SCORE_MAP[i], b = SCORE_MAP[i + 1];
    if (payment >= a.payment && payment <= b.payment) {
      const t = (payment - a.payment) / (b.payment - a.payment);
      return Math.round(a.gain + t * (b.gain - a.gain));
    }
  }
  return 55;
}

const ScoreSimulator = ({ onCta }) => {
  const [payment, setPayment] = useState(300);
  const gain = interpolateGain(payment);
  const newScore = BASE_SCORE + gain;
  const pct = ((payment - 100) / 400) * 100;

  // Gauge SVG
  const gaugeR = 58, gaugeCx = 70, gaugeCy = 68;
  const gaugeArc = 2 * Math.PI * gaugeR * 0.65;
  const gaugePct = ((newScore - 300) / 550) * 100;
  const gaugeOffset = gaugeArc - (gaugeArc * gaugePct) / 100;

  return (
    <section className="sim-section" data-testid="score-simulator">
      <div className="container">
        <h2 className="sim-headline">See how small payment changes can affect your score</h2>
        <p className="sim-subtext">Move the slider to preview how increasing your payment could improve your score.</p>

        <div className="sim-card">
          <div className="sim-card-inner">
            {/* Left: Score Display */}
            <div className="sim-left">
              <div className="sim-label">Current Score</div>
              <div className="sim-scores">
                <span className="sim-current">{BASE_SCORE}</span>
                <span className="sim-arrow-icon">
                  <svg width="28" height="18" viewBox="0 0 28 18"><path d="M2 9h20m0 0l-5-5m5 5l-5 5" stroke="#16a34a" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="sim-new">{newScore}</span>
              </div>
              <div className="sim-gain-badge">+{gain} pts <span className="sim-gain-time">in 6 months</span></div>

              {/* Slider */}
              <div className="sim-slider-section">
                <div className="sim-slider-label">Monthly Payment Toward Debt</div>
                <div className="sim-slider-wrap">
                  <div className="sim-slider-track">
                    <div className="sim-slider-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="500"
                    step="10"
                    value={payment}
                    onChange={(e) => setPayment(parseInt(e.target.value))}
                    className="sim-slider-input"
                    data-testid="sim-slider"
                  />
                </div>
                <div className="sim-slider-ticks">
                  {[100, 200, 300, 400, 500].map(v => (
                    <span key={v} className={`sim-tick ${payment >= v ? 'sim-tick-active' : ''}`}>${v}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Gauge */}
            <div className="sim-right">
              <svg width="140" height="100" viewBox="0 0 140 100" className="sim-gauge-svg">
                <defs>
                  <linearGradient id="simGauge" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f97316" />
                    <stop offset="40%" stopColor="#eab308" />
                    <stop offset="70%" stopColor="#22c55e" />
                    <stop offset="100%" stopColor="#16a34a" />
                  </linearGradient>
                </defs>
                <circle cx={gaugeCx} cy={gaugeCy} r={gaugeR} fill="none" stroke="#e2e8f0" strokeWidth="14"
                  strokeDasharray={`${gaugeArc} ${2*Math.PI*gaugeR}`} strokeLinecap="round"
                  transform={`rotate(150 ${gaugeCx} ${gaugeCy})`} />
                <circle cx={gaugeCx} cy={gaugeCy} r={gaugeR} fill="none" stroke="url(#simGauge)" strokeWidth="14"
                  strokeDasharray={`${gaugeArc} ${2*Math.PI*gaugeR}`} strokeDashoffset={gaugeOffset} strokeLinecap="round"
                  transform={`rotate(150 ${gaugeCx} ${gaugeCy})`}
                  style={{ transition: 'stroke-dashoffset 0.4s ease' }} />
              </svg>
              <div className="sim-gauge-inner">
                <div className="sim-gauge-label">Estimated Score</div>
                <div className="sim-gauge-scores">
                  <span className="sim-gauge-from">{BASE_SCORE}</span>
                  <span className="sim-gauge-arr">
                    <svg width="16" height="12" viewBox="0 0 16 12"><path d="M1 6h11m0 0l-3-3m3 3l-3 3" stroke="#16a34a" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  <span className="sim-gauge-to">{newScore}</span>
                </div>
                <div className="sim-gauge-gain">+{gain} pts <span>in 6 months</span></div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="sim-disclaimer">
            Estimates based on typical credit behavior. Your personalized plan will provide exact recommendations.
          </p>
        </div>

        {/* CTA */}
        <button className="sim-cta" onClick={onCta} data-testid="sim-cta-btn">
          Unlock My Best Payment Strategy
        </button>
      </div>
    </section>
  );
};

/* ═══ PWA INSTALL PROMPT ═══ */
const InstallApp = () => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    setIsIos(/iPad|iPhone|iPod/.test(ua));
    setIsAndroid(/Android/.test(ua));
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      setShowInstructions(true);
    }
  };

  if (isInstalled) return null;

  return (
    <>
      <button className="pwa-install-btn" onClick={handleInstall} data-testid="pwa-install-btn">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2v10m0 0l-3.5-3.5M10 12l3.5-3.5M4 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Install Credit GPS App
      </button>

      {showInstructions && (
        <div className="pwa-modal-bg" onClick={() => setShowInstructions(false)}>
          <div className="pwa-modal" onClick={e => e.stopPropagation()}>
            <button className="pwa-modal-close" onClick={() => setShowInstructions(false)}>✕</button>
            <h3 className="pwa-modal-title">Install Credit GPS</h3>
            <p className="pwa-modal-sub">Add to your home screen for the best experience.</p>

            {isIos ? (
              <div className="pwa-steps">
                <div className="pwa-step">
                  <span className="pwa-step-num">1</span>
                  <div>
                    <strong>Tap the Share button</strong>
                    <p>At the bottom of Safari, tap the share icon (square with an arrow pointing up).</p>
                  </div>
                </div>
                <div className="pwa-step">
                  <span className="pwa-step-num">2</span>
                  <div>
                    <strong>Scroll down and tap "Add to Home Screen"</strong>
                    <p>It has a (+) icon next to it.</p>
                  </div>
                </div>
                <div className="pwa-step">
                  <span className="pwa-step-num">3</span>
                  <div>
                    <strong>Tap "Add"</strong>
                    <p>Credit GPS will appear on your home screen like a regular app.</p>
                  </div>
                </div>
              </div>
            ) : isAndroid ? (
              <div className="pwa-steps">
                <div className="pwa-step">
                  <span className="pwa-step-num">1</span>
                  <div>
                    <strong>Tap the menu icon</strong>
                    <p>In Chrome, tap the three dots (&#8942;) in the top-right corner.</p>
                  </div>
                </div>
                <div className="pwa-step">
                  <span className="pwa-step-num">2</span>
                  <div>
                    <strong>Tap "Add to Home screen"</strong>
                    <p>Or "Install app" if you see that option.</p>
                  </div>
                </div>
                <div className="pwa-step">
                  <span className="pwa-step-num">3</span>
                  <div>
                    <strong>Tap "Add" or "Install"</strong>
                    <p>Credit GPS will be added to your home screen and app drawer.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="pwa-steps">
                <div className="pwa-step">
                  <span className="pwa-step-num">1</span>
                  <div>
                    <strong>iPhone (Safari)</strong>
                    <p>Tap Share (square + arrow) then "Add to Home Screen".</p>
                  </div>
                </div>
                <div className="pwa-step">
                  <span className="pwa-step-num">2</span>
                  <div>
                    <strong>Android (Chrome)</strong>
                    <p>Tap &#8942; menu then "Add to Home screen" or "Install app".</p>
                  </div>
                </div>
              </div>
            )}

            <button className="pwa-modal-done" onClick={() => setShowInstructions(false)}>Got It</button>
          </div>
        </div>
      )}
    </>
  );
};

const LandingPage = () => {
  const [selectedTimeline, setSelectedTimeline] = useState(6);
  const [selectedGoalScore, setSelectedGoalScore] = useState(720);
  const [customGoalScore, setCustomGoalScore] = useState('');
  const [monthlyBudget, setMonthlyBudget] = useState(300);
  const [customBudget, setCustomBudget] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // IMPORTANT: Landing page should NEVER show personalized data
  // Clear any stale session data when user visits landing page
  // This prevents showing old cached data to new visitors
  useEffect(() => {
    // Clear old session data to ensure fresh experience
    localStorage.removeItem('creditSessionId');
    localStorage.removeItem('hasPaid');
    localStorage.removeItem('betaCode');
    localStorage.removeItem('isPro');
    localStorage.removeItem('userName');
    localStorage.removeItem('selectedTimeline');
    localStorage.removeItem('selectedGoalScore');
    localStorage.removeItem('monthlyBudget');
  }, []);

  const timelines = [
    { months: 6, label: '6 Months' },
    { months: 12, label: '12 Months' },
    { months: 18, label: '18 Months' },
    { months: 24, label: '24 Months' }
  ];

  const goalScores = [720, 750, 825, 850];

  const handleGoalScoreSelect = (score) => {
    setSelectedGoalScore(score);
    setCustomGoalScore('');
  };

  const handleCustomGoalChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 300 && parseInt(value) <= 850)) {
      setCustomGoalScore(value);
      if (value) {
        setSelectedGoalScore(parseInt(value));
      }
    }
  };

  const budgetOptions = [200, 300, 500, 750];

  const handleBudgetSelect = (budget) => {
    setMonthlyBudget(budget);
    setCustomBudget('');
  };

  const handleCustomBudgetChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 50 && parseInt(value) <= 5000)) {
      setCustomBudget(value);
      if (value) {
        setMonthlyBudget(parseInt(value));
      }
    }
  };

  const handleGetStarted = () => {
    localStorage.setItem('selectedTimeline', selectedTimeline);
    localStorage.setItem('selectedGoalScore', selectedGoalScore);
    localStorage.setItem('monthlyBudget', monthlyBudget);
    navigate('/checkout', { state: { timeline: selectedTimeline, goalScore: selectedGoalScore, monthlyBudget: monthlyBudget } });
  };

  const handleExplore = (section) => {
    localStorage.setItem('creditSessionId', 'demo-explore');
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'resources') {
      navigate('/dashboard', { state: { activeTab: 'resources' } });
    }
    setMenuOpen(false);
  };

  return (
    <div className="landing-page">
      {/* Premium Header with Hamburger */}
      <header className="landing-header">
        <div className="landing-header-content">
          <div className="header-logo-section">
            <div className="header-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/sc61wlvn_IMG_3537.jpeg" 
                alt="Credit GPS Logo"
                width="40"
                height="40"
                style={{ borderRadius: '50%' }}
              />
            </div>
            <span className="header-brand-name">Credit GPS</span>
          </div>
          <button 
            className="hamburger-btn" 
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="hamburger-menu-btn"
          >
            <span className="hamburger-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </header>

      {/* Hamburger Menu Overlay */}
      {menuOpen && (
        <div className="menu-overlay-landing" onClick={() => setMenuOpen(false)}>
          <div className="menu-drawer-landing" onClick={(e) => e.stopPropagation()}>
            <div className="menu-header-landing">
              <h3>Explore Credit GPS</h3>
              <button className="menu-close" onClick={() => setMenuOpen(false)}>✕</button>
            </div>
            
            {/* Upload Button in Menu */}
            <div className="menu-upload-section">
              <button className="menu-upload-btn" onClick={handleGetStarted}>
                Upload Your Credit Report
              </button>
              <p className="menu-upload-hint">Get your personalized plan in minutes</p>
            </div>
            
            {/* Sample Dashboard Preview */}
            <div className="menu-dashboard-preview">
              <h4 className="preview-title">Sample Dashboard Preview</h4>
              <p className="preview-desc">Here's what your plan will look like:</p>
              <img 
                src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/iftsw4ev_IMG_3718.jpeg"
                alt="Sample Dashboard"
                className="preview-dashboard-image"
              />
              <p className="preview-note">
                This is a sample. Your actual dashboard will be personalized based on your credit report.
              </p>
            </div>
            
            <div className="menu-items-landing">
              <button className="menu-item-landing" onClick={() => handleExplore('resources')}>
                <span className="menu-icon">📚</span>
                <div>
                  <div className="menu-item-title">Credit Resources</div>
                  <div className="menu-item-desc">Learn about credit improvement</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          NEW OUTCOME-DRIVEN HERO SECTION (DARK - FIRST)
          ============================================ */}
      <section className="outcome-hero">
        <div className="outcome-hero-container">
          {/* SECTION 1: Generic Headline - NEVER personalized on landing page */}
          <h1 className="outcome-headline" data-testid="outcome-headline">
            Your next credit move is ready.
          </h1>

          {/* SECTION 2: Outcome Promise */}
          <p className="outcome-promise">
            Raise your score by an estimated <span className="highlight-gain">+10–25 points</span> in the next 30 days — starting today.
          </p>

          {/* SECTION 3: Live Preview Card */}
          <div className="live-preview-card" data-testid="live-preview-card">
            <div className="preview-card-header">
              <span className="preview-badge">YOUR FIRST MOVE</span>
            </div>
            <div className="preview-card-body">
              <div className="preview-payment-row">
                <span className="preview-pay-label">Pay</span>
                <span className="preview-pay-amount">$50</span>
                <span className="preview-arrow">→</span>
                <span className="preview-account">Credit One ••7710</span>
              </div>
              <div className="preview-impact-row">
                <div className="preview-util">
                  <span className="util-change">81% → 77%</span>
                  <span className="util-label">utilization</span>
                </div>
                <div className="preview-score">
                  <span className="score-change">+10 to +16 points</span>
                  <span className="score-label">High Impact</span>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 4: Clarity Statement */}
          <p className="clarity-statement">
            Stop guessing. Start executing.
          </p>

          {/* SECTION 5: CTA */}
          <button 
            className="outcome-cta-btn" 
            onClick={handleGetStarted}
            data-testid="outcome-cta-btn"
          >
            Get My Action Plan
          </button>

          {/* SECTION 6: Trust Line */}
          <p className="trust-line">
            No subscriptions. Instant plan. AI-powered precision.
          </p>

          {/* PWA Install Button */}
          <InstallApp />
        </div>
      </section>

      {/* ============================================
          HERO BRANDING SECTION (WHITE - SECOND)
          ============================================ */}
      <section className="hero-branding-section">
        <div className="hero-branding-container">
          <div className="hero-branding-split">
            <div className="hero-branding-left">
              <div className="hero-brand-lockup">
                <img 
                  src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/sc61wlvn_IMG_3537.jpeg" 
                  alt="Credit GPS"
                  className="hero-brand-logo-large"
                />
                <span className="hero-brand-name-large">Credit GPS</span>
              </div>

              <h2 className="hero-headline-large">
                Get Your AI Credit<br />Action Plan in Minutes
              </h2>
              
              <p className="hero-subheadline-large">
                Upload your credit report, choose your timeline, and get a clear step-by-step roadmap & plan built for your exact situation — so you know exactly what debt and bills to pay and when to pay it.
              </p>
            </div>

            <div className="hero-branding-right">
              <div className="hero-visual-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/gdsczjf6_IMG_3714.png"
                  alt="Credit GPS Roadmap"
                  className="hero-visual-image"
                />
                <div className="hero-timeline-gauge">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/3l10hvmo_IMG_3716.jpeg"
                    alt="Goal Timeline Gauge"
                    className="hero-gauge-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Selection Section */}
      <section className="timeline-section-top">
        <div className="container">
          <h2 className="section-headline-timeline">
            Choose your timeline and get a clear path to your ideal credit score— guided step-by-step by AI.
          </h2>

          <div className="timeline-selector-compact">
            {timelines.map((timeline) => (
              <button
                key={timeline.months}
                data-testid={`timeline-btn-${timeline.months}`}
                className={`timeline-btn-compact ${selectedTimeline === timeline.months ? 'active' : ''}`}
                onClick={() => setSelectedTimeline(timeline.months)}
              >
                {timeline.label}
              </button>
            ))}
          </div>

          {/* Goal Score Selector */}
          <div className="goal-score-section">
            <h3 className="goal-score-label">What's your target credit score?</h3>
            <div className="goal-score-selector">
              {goalScores.map((score) => (
                <button
                  key={score}
                  data-testid={`goal-btn-${score}`}
                  className={`goal-score-btn ${selectedGoalScore === score && !customGoalScore ? 'active' : ''}`}
                  onClick={() => handleGoalScoreSelect(score)}
                >
                  {score}
                </button>
              ))}
              <div className="custom-goal-input-wrapper">
                <input
                  type="number"
                  placeholder="Custom"
                  value={customGoalScore}
                  onChange={handleCustomGoalChange}
                  className={`custom-goal-input ${customGoalScore ? 'active' : ''}`}
                  min="300"
                  max="850"
                  data-testid="custom-goal-input"
                />
              </div>
            </div>
            <p className="goal-score-hint">
              Selected: <strong>{selectedGoalScore}</strong> credit score
            </p>
          </div>

          {/* Monthly Budget Selector */}
          <div className="budget-section">
            <h3 className="budget-label">How much can you put toward credit each month?</h3>
            <div className="budget-selector">
              {budgetOptions.map((budget) => (
                <button
                  key={budget}
                  data-testid={`budget-btn-${budget}`}
                  className={`budget-btn ${monthlyBudget === budget && !customBudget ? 'active' : ''}`}
                  onClick={() => handleBudgetSelect(budget)}
                >
                  ${budget}
                </button>
              ))}
              <div className="custom-budget-input-wrapper">
                <input
                  type="number"
                  placeholder="Custom"
                  value={customBudget}
                  onChange={handleCustomBudgetChange}
                  className={`custom-budget-input ${customBudget ? 'active' : ''}`}
                  min="50"
                  max="5000"
                  data-testid="custom-budget-input"
                />
              </div>
            </div>
            <p className="budget-hint">
              Your plan will allocate <strong>${monthlyBudget}/month</strong> across your accounts
            </p>
          </div>

          <button className="cta-btn-large" onClick={handleGetStarted} data-testid="get-started-btn">
            Get My Plan
          </button>
        </div>
      </section>

      {/* ============================================
          SOCIAL PROOF + TESTIMONIALS SECTION
          ============================================ */}
      <section className="social-proof-section" data-testid="social-proof-section">
        <div className="container">
          <div className="social-proof-image-wrap">
            <img 
              src="https://customer-assets.emergentagent.com/job_677e7a8a-aea4-44eb-9785-abfbfb7cf099/artifacts/jjy1w6uy_IMG_4351.jpeg"
              alt="People are already improving their credit with Credit GPS — Trusted by users nationwide"
              className="social-proof-img"
              data-testid="social-proof-image"
            />
          </div>
        </div>
      </section>

      {/* ============================================
          INTERACTIVE SCORE IMPACT SIMULATOR
          ============================================ */}
      <ScoreSimulator onCta={handleGetStarted} />

      {/* Problem/Agitate Section - Compact with Bullets */}
      <section className="problem-section-compact">
        <div className="container">
          <h2 className="section-headline-compact">Trying to Fix Your Credit But Not Sure What to Do First?</h2>
          
          <p className="section-intro-compact">You're not alone.</p>
          
          <div className="bullet-list">
            <div className="bullet-item-check">
              <span className="bullet-check">✓</span>
              <p>Don't know what actually impacts their score</p>
            </div>
            <div className="bullet-item-check">
              <span className="bullet-check">✓</span>
              <p>Get conflicting advice</p>
            </div>
            <div className="bullet-item-check">
              <span className="bullet-check">✓</span>
              <p>Waste time doing the wrong things first</p>
            </div>
          </div>

          <p className="problem-result-compact">
            And that leads to frustration, delays, and unnecessary stress.
          </p>
        </div>
      </section>

      {/* Solution Section - Compact with Logo */}
      <section className="solution-section-compact">
        <div className="container">
          <div className="solution-header-with-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/sc61wlvn_IMG_3537.jpeg" 
              alt="Credit GPS"
              className="solution-logo"
            />
            <h2 className="section-headline-compact">Credit GPS Gives You a Clear Starting Point</h2>
          </div>
          <p className="solution-text-compact">
            Instead of guessing, you get a personalized plan based on your situation and your timeline.
          </p>
          <p className="solution-text-compact">
            So you can move forward with confidence — not confusion.
          </p>
          
          {/* ANALYZED BY AI Badge */}
          <div className="analyzed-by-ai-badge">
            <img 
              src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/o6zn0iw4_IMG_3719.png"
              alt="Analyzed by AI"
              className="ai-badge-image"
            />
          </div>
        </div>
      </section>

      {/* How It Works - Compact with Numbered Bullets */}
      <section className="how-it-works-compact">
        <div className="container">
          <h2 className="section-headline-compact">How It Works</h2>
          
          <div className="numbered-list">
            <div className="numbered-item">
              <div className="number-badge">1</div>
              <div className="numbered-content">
                <div className="numbered-title-with-button">
                  <h3 className="numbered-title">Upload Your Credit Report</h3>
                  <button className="upload-btn-inline" onClick={handleGetStarted}>
                    Upload Report
                  </button>
                </div>
                <p className="numbered-desc">Securely upload your report to get started.</p>
              </div>
            </div>
            
            <div className="numbered-item">
              <div className="number-badge">2</div>
              <div className="numbered-content">
                <h3 className="numbered-title">Choose Your Timeline</h3>
                <p className="numbered-desc">Pick how fast you want to reach your goal.</p>
              </div>
            </div>
            
            <div className="numbered-item">
              <div className="number-badge">3</div>
              <div className="numbered-content">
                <h3 className="numbered-title">Get Your Action Plan</h3>
                <p className="numbered-desc">Receive a clear, step-by-step roadmap built for you.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section with Box */}
      <section className="what-you-get-section">
        <div className="container">
          <div className="section-box">
            <h2 className="section-headline-compact">What You'll Get</h2>
            
            <div className="bullet-list">
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Step-by-step action plan</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Clear priorities (what to do first)</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Timeline-based strategy</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Progress guidance</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Personalized recommendations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ANALYZED BY AI Badge - Between Sections */}
      <section className="ai-badge-section">
        <div className="container">
          <div className="analyzed-by-ai-badge">
            <img 
              src="https://customer-assets.emergentagent.com/job_credit-gps-demo/artifacts/o6zn0iw4_IMG_3719.png"
              alt="Analyzed by AI"
              className="ai-badge-image"
            />
          </div>
        </div>
      </section>

      {/* From Confusion to Clarity with Box */}
      <section className="clarity-section">
        <div className="container">
          <div className="section-box">
            <h2 className="section-headline-compact">From Confusion to Clarity</h2>
            
            <div className="bullet-list">
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Know exactly what to do first</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Stop wasting time on the wrong things</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Move forward with confidence</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Track your progress</p>
              </div>
              <div className="bullet-item-check">
                <span className="bullet-check">✓</span>
                <p>Reach your credit goals faster</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props - Simplified */}
      <section className="value-section-compact">
        <div className="container">
          <h2 className="section-headline-compact">Most people don't need more services — they need a clear plan.</h2>
          <p className="value-text-compact">
            Stop paying for confusing services. Start with a clear plan you control.
          </p>
          <p className="value-text-compact">
            No guessing. No confusion. Just a clear plan based on your report.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container-narrow">
          <h2 className="section-headline-center">Unlock Your Personalized Credit Plan</h2>
          <div className="price-display">$29<span className="price-period">one-time</span></div>
          
          <div className="whats-included">
            <h3 className="included-title">What's Included:</h3>
            <ul className="included-list">
              <li>✓ Your personalized action plan</li>
              <li>✓ Priority steps based on your report</li>
              <li>✓ Timeline-based roadmap</li>
              <li>✓ Instant access</li>
            </ul>
          </div>

          <button className="cta-btn-large cta-btn-final" onClick={handleGetStarted}>
            Get My Plan Now
          </button>

          <p className="final-trust-line">No subscriptions required. Get your plan instantly.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <p className="footer-text">Credit GPS © 2024. Your personalized path to better credit.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
